import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { FC } from 'react';
import { Fragment } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { PreferredSide } from '#src/__generated__/globalTypes.ts';
import { isType } from '#src/lib/gql/index.ts';

import type { PlayerHandsInfo_AnyPlayerInterface } from './__generated__/index.graphql.ts';

const battingThrowingHandMessage = defineMessages({
  [PreferredSide.LEFT]: {
    id: 'MLB.playerStats.battingAndThrowingHands.Left',
    defaultMessage: 'Left',
  },
  [PreferredSide.RIGHT]: {
    id: 'MLB.playerStats.battingAndThrowingHands.Right',
    defaultMessage: 'Right',
  },
  [PreferredSide.BOTH]: {
    id: 'MLB.playerStats.battingAndThrowingHands.Both',
    defaultMessage: 'Switch',
  },
});

type Props = {
  as?: FC;
  player: PlayerHandsInfo_AnyPlayerInterface;
};

// This component needs to be in core because we're using it in the marketplace package.
export const PlayerHandsInfo = ({ as, player }: Props) => {
  if (!isType(player, 'BaseballPlayer')) return null;

  const Wrapper = as || Fragment;
  return (
    <Wrapper>
      {player.batHand ? (
        <FormattedMessage {...battingThrowingHandMessage[player.batHand]} />
      ) : (
        '-'
      )}
      &nbsp;/&nbsp;
      {player.throwHand ? (
        <FormattedMessage {...battingThrowingHandMessage[player.throwHand]} />
      ) : (
        '-'
      )}
    </Wrapper>
  );
};

PlayerHandsInfo.fragments = {
  AnyPlayerInterface: gql`
    fragment PlayerHandsInfo_AnyPlayerInterface on AnyPlayerInterface {
      slug
      ... on BaseballPlayer {
        slug
        batHand
        throwHand
      }
    }
  ` as TypedDocumentNode<PlayerHandsInfo_AnyPlayerInterface>,
};
